/* eslint-disable no-return-assign */
/* eslint-disable no-unused-vars */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import closeSvg from 'src/assets/close.svg';
import { CommonInput, FormErrorWrap, FullScreenLoading, RegularButton } from 'pg-ui-library';
import ReactModal from 'react-modal';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { useActions } from 'src/hooks/useActions';
import personalInfoService from 'src/services/personal-info.service';
import { phoneRegExp } from 'src/utils/phoneRegex';
import { Mutation } from '@tanstack/react-query';
import { CloseIcon, UserForm, FormInputWrapper, ActionWrapper } from './styles';
import { useAddDependent, useUpdateDependent } from '../../mutations/add-delete-update-dependent.mutation';

const PersonalUserForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const addDependentToEmploye = useAddDependent();
  const updateDependentToEmployee = useUpdateDependent();
  const { getPersonalInfo } = useActions();
  const { data } = useTypedSelector(state => state.personalInfo);

  const [isSubmitting, setIsSubmitting] = useState(false); // State for tracking loading

  const validationSchema = yup.object({
    username: yup
      .string()
      .required(t('global.form.required'))
      .matches(/^(?=.*[a-zA-Z])[A-Za-z0-9\-_@.]{4,}$/, t('global.form.invalidFormat')),
    secondaryEmail: yup.string().required(t('global.form.required')).email(t('global.form.email')),
    phone1: yup.string().min(1, 'Too small').max(30, 'Too large').matches(phoneRegExp, t('global.form.invalidPhone')),
    phone2: yup.string().min(1, 'Too small').max(30, 'Too large').matches(phoneRegExp, t('global.form.invalidPhone')),
  });

  const formik = useFormik({
    initialValues: {
      username: data.personalInfo?.username || '',
      secondaryEmail: data.personalInfo?.address.secondaryEmail || '',
      phone1: data.personalInfo?.address.phone1 || '',
      phone2: data.personalInfo?.address.phone2 || '',
    },
    validationSchema,
    onSubmit: async (values: { username: string; secondaryEmail: string; phone1: string; phone2: string }) => {
      setIsSubmitting(true);
      await personalInfoService.patchPersonalInformation({
        ...data.personalInfo,
        phone1: values.phone1,
        username: values.username,
        secondaryEmail: values.secondaryEmail,
        phone2: values.phone2,
      });
      getPersonalInfo();
      setIsSubmitting(false);
      onSuccess();
    },
  });

  useEffect(() => {
    getPersonalInfo();
  }, []);

  return (
    <>
      {isSubmitting && <FullScreenLoading />}
      <UserForm onSubmit={formik.handleSubmit}>
        <FormInputWrapper>
          <CommonInput
            id="username"
            name="username"
            label={`${t('personalInfo.generalInfo.username')}*`}
            placeholder={t('personalInfo.generalInfo.username')}
            value={formik.values.username}
            onChange={event => formik.setFieldValue('username', event.target.value)}
            style={{ width: '100%' }}
          />
          <FormErrorWrap errorMessage={formik.errors.username as string} visible={!!formik.errors.username} />
        </FormInputWrapper>
        <FormInputWrapper>
          <CommonInput
            id="secondaryEmail"
            name="secondaryEmail"
            label={`${t('personalInfo.address.secondaryEmail')}*`}
            placeholder={t('personalInfo.address.secondaryEmail')}
            value={formik.values.secondaryEmail}
            onChange={event => formik.setFieldValue('secondaryEmail', event.target.value)}
            style={{ width: '100%' }}
          />
          <FormErrorWrap
            errorMessage={formik.errors.secondaryEmail as string}
            visible={!!formik.errors.secondaryEmail}
          />
        </FormInputWrapper>
        <FormInputWrapper style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ flex: '0 0 48%' }}>
            <CommonInput
              id="phone1"
              name="phone1"
              label="Phone 1"
              placeholder={t('personalInfo.address.phone1')}
              value={formik.values.phone1}
              onChange={event => formik.setFieldValue('phone1', event.target.value)}
              style={{ width: '100%' }}
            />
            <FormErrorWrap errorMessage={formik.errors.phone1 as string} visible={!!formik.errors.phone1} />
          </div>

          <div style={{ flex: '0 0 48%' }}>
            <CommonInput
              id="phone2"
              name="phone2"
              label="Phone 2"
              placeholder={t('personalInfo.address.phone2')}
              value={formik.values.phone2}
              onChange={event => formik.setFieldValue('phone2', event.target.value)}
              style={{ width: '100%' }}
            />
            <FormErrorWrap errorMessage={formik.errors.phone2 as string} visible={!!formik.errors.phone2} />
          </div>
        </FormInputWrapper>
      </UserForm>
      <ActionWrapper>
        <RegularButton
          type="submit"
          onClick={() => formik.handleSubmit()}
          disabled={
            !formik.isValid || isSubmitting || addDependentToEmploye.isLoading || updateDependentToEmployee.isLoading
          }
        >
          {t('global.form.save')}
        </RegularButton>
      </ActionWrapper>
    </>
  );
};

export const PersonalUserModal: React.FC<{
  isOpen: boolean;
  setIsOpen: Function;
}> = ({ isOpen, setIsOpen }) => {
  const modalRef = useRef(null);

  const handleModalClose = () => {
    setIsOpen(false);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: 'max-content',
          maxWidth: '600px',
          height: 'max-content',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        },
      }}
      onRequestClose={handleModalClose}
      contentRef={(node: any) => (modalRef.current = node)}
    >
      <CloseIcon src={closeSvg} onClick={handleModalClose} />
      <PersonalUserForm onSuccess={handleModalClose} />
    </ReactModal>
  );
};
